import { useState, useEffect, useCallback, useMemo } from "react";
import { ArrowLeft, Plus, Eye, Heart, Pause, Play, Trash2, Edit, CheckCircle, Package, Loader2, Share2, Tag } from "lucide-react";
import { Button } from "@/app/components/button";
import type { Ad } from "@/app/components/ad-card";
import { formatAdCategoryDisplayLine, getAdImage } from "@/app/utils/ad-utils";
import { AdImage } from "@/app/components/common/ad-image";
import { BottomNav } from "./bottom-nav";
import { formatCurrency } from "@/config/locale";
import { EditAdModal } from "@/app/components/edit-ad-modal";
import { getMyAds, apiAdToAd, patchAd, deleteAd, getListingQualityForAd, type ApiAdResponse, type ListingQualityResult } from "@/app/api/auth-client";
import { ListingQualityPanel } from "@/app/components/listing-quality-panel";
import { hasPersistedListingQuality, shouldShowListingQualityImproveLink } from "@/app/utils/listing-quality-utils";
import { shareAdListing } from "@/app/utils/share-ad-listing";

export type AdStatus = "active" | "paused" | "sold" | "expired";

export interface MyAd extends Ad {
  status: AdStatus;
  /** Sambora raw status. `paused` = reservado por troca (1.º pagamento); `draft` = pausa manual do vendedor. */
  apiListingStatus?: string;
  /** False when listing fails discovery quality gates (e.g. missing photos). */
  discoveryEligible?: boolean;
  listingQualityScore?: number;
  listingQualityTier?: string;
  listingQualityVersion?: string;
  views: number;
  favorites: number;
}

/** Ativos no topo, depois vendidos, depois inativos (pausado / expirado). */
function myAdsStatusSortRank(status: AdStatus): number {
  if (status === "active") return 0;
  if (status === "sold") return 1;
  return 2;
}

function sortMyAdsForDisplay(list: MyAd[]): MyAd[] {
  return [...list].sort((a, b) => {
    const diff = myAdsStatusSortRank(a.status) - myAdsStatusSortRank(b.status);
    if (diff !== 0) return diff;
    const ta = a.createdAt?.trim() ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt?.trim() ? new Date(b.createdAt).getTime() : 0;
    const na = Number.isNaN(ta) ? 0 : ta;
    const nb = Number.isNaN(tb) ? 0 : tb;
    return nb - na;
  });
}

/** Map API status to MyAd status (active, draft|paused, sold, archived -> active, paused, sold, expired). */
function apiStatusToAdStatus(status: string): AdStatus {
  switch (status) {
    case "active":
      return "active";
    case "draft":
    case "paused":
      return "paused";
    case "sold":
      return "sold";
    case "archived":
      return "expired";
    default:
      return "active";
  }
}

function apiAdToMyAd(api: ApiAdResponse): MyAd {
  const ad = apiAdToAd(api);
  const raw = (api.status ?? "active").toLowerCase().trim();
  return {
    ...ad,
    status: apiStatusToAdStatus(api.status ?? "active"),
    apiListingStatus: raw,
    discoveryEligible: api.discovery_eligible !== false,
    listingQualityScore: api.listing_quality_score,
    listingQualityTier: api.listing_quality_tier,
    listingQualityVersion: api.listing_quality_version,
    views: 0,
    favorites: 0,
  };
}

interface MyAdsPageProps {
  onBack: () => void;
  onCreateAd: () => void;
  /** Called after an ad is successfully deleted (e.g. to invalidate categories-with-ads cache). */
  onAdDeleted?: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  /** Increment to refetch list (e.g. after creating an ad). */
  refreshTrigger?: number;
}

type TabType = "all" | "active" | "paused" | "sold" | "expired";

export function MyAdsPage({ onBack, onCreateAd, onAdDeleted, activeTab, onTabChange, favoritesCount, onMenuClick, onPostAdClick, postAdBlockedByUnverifiedEmail = false, refreshTrigger = 0 }: MyAdsPageProps) {
  const [ads, setAds] = useState<MyAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeAdsTab, setActiveAdsTab] = useState<TabType>("all");
  const [editingAd, setEditingAd] = useState<MyAd | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchAds = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const list = await getMyAds();
      setAds(list.map(apiAdToMyAd));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Não foi possível carregar seus anúncios.";
      if (!options?.silent) {
        setError(msg);
        setAds([]);
      }
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAds();
  }, [fetchAds, refreshTrigger]);

  const handleTogglePause = async (id: string) => {
    const ad = ads.find((a) => a.id === id);
    if (!ad) return;
    if (ad.status === "paused" && ad.apiListingStatus === "paused") {
      setActionError(
        "Este anúncio está reservado num pedido de troca. Só volta ao ar quando a troca for concluída ou o pedido for cancelado."
      );
      return;
    }
    const newApiStatus = ad.status === "paused" ? "active" : "draft";
    setActionLoadingId(id);
    setActionError(null);
    try {
      const updated = await patchAd(id, { status: newApiStatus });
      setAds((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                ...apiAdToMyAd(updated),
                status: apiStatusToAdStatus(updated.status ?? "active"),
                views: a.views,
                favorites: a.favorites,
              }
            : a
        )
      );
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Não foi possível alterar o status.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleMarkAsSold = async (id: string) => {
    setActionLoadingId(id);
    setActionError(null);
    try {
      const updated = await patchAd(id, { status: "sold" });
      setAds((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                ...apiAdToMyAd(updated),
                status: "sold" as AdStatus,
                views: a.views,
                favorites: a.favorites,
              }
            : a
        )
      );
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Não foi possível marcar como vendido.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este anúncio?")) return;
    setActionLoadingId(id);
    setActionError(null);
    try {
      await deleteAd(id);
      setAds((prev) => prev.filter((ad) => ad.id !== id));
      onAdDeleted?.();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Não foi possível excluir o anúncio.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSaveEdit = (updatedAd: MyAd) => {
    setEditingAd(null);
    setAds((prev) => prev.map((ad) => (ad.id === updatedAd.id ? updatedAd : ad)));
    void fetchAds({ silent: true });
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const filteredAds = useMemo(() => {
    const list = ads.filter((ad) => {
      if (activeAdsTab === "all") return true;
      return ad.status === activeAdsTab;
    });
    return sortMyAdsForDisplay(list);
  }, [ads, activeAdsTab]);

  const getStatusCount = (status: TabType) => {
    if (status === "all") return ads.length;
    return ads.filter((ad) => ad.status === status).length;
  };

  const tabs: { id: TabType; label: string; count: number }[] = [
    { id: "all", label: "Todos", count: getStatusCount("all") },
    { id: "active", label: "Ativos", count: getStatusCount("active") },
    { id: "paused", label: "Pausados", count: getStatusCount("paused") },
    { id: "sold", label: "Vendidos", count: getStatusCount("sold") },
    { id: "expired", label: "Expirados", count: getStatusCount("expired") },
  ];

  const postAdEmailBlockTitle = "Confirme seu e-mail para criar anúncios";

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl text-white">Meus Anúncios</h1>
              <p className="text-sm text-white/80">
                Gerencie todos os seus anúncios
              </p>
            </div>
            <Button
              type="button"
              onClick={onCreateAd}
              title={postAdBlockedByUnverifiedEmail ? postAdEmailBlockTitle : undefined}
              aria-disabled={postAdBlockedByUnverifiedEmail}
              className={`gap-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-0 ${postAdBlockedByUnverifiedEmail ? "opacity-60 saturate-50" : ""}`}
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Novo Anúncio</span>
            </Button>
          </div>

          {/* Tabs: carrossel horizontal (igual Ofertas Recebidas / Enviadas no mobile). */}
          <div
            role="tablist"
            aria-label="Filtrar anúncios por status"
            className="flex min-w-0 flex-nowrap gap-2 overflow-x-auto pb-2 -mb-2 hide-scrollbar scroll-smooth"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeAdsTab === tab.id}
                onClick={() => setActiveAdsTab(tab.id)}
                className={`shrink-0 whitespace-nowrap px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeAdsTab === tab.id
                    ? "bg-white text-primary"
                    : "bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {actionError && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center justify-between gap-4">
            <p className="text-destructive text-sm">{actionError}</p>
            <button
              type="button"
              onClick={() => setActionError(null)}
              className="text-destructive hover:underline text-sm shrink-0"
            >
              Fechar
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
            <p className="text-muted-foreground">Carregando seus anúncios...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-destructive mb-4">{error}</p>
            <Button variant="outline" onClick={() => fetchAds()}>
              Tentar novamente
            </Button>
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-secondary-foreground" />
            </div>
            <h3 className="mb-2">Nenhum anúncio encontrado</h3>
            <p className="text-muted-foreground mb-6">
              {activeAdsTab === "all"
                ? "Você ainda não criou nenhum anúncio"
                : `Você não tem anúncios ${tabs.find((t) => t.id === activeAdsTab)?.label.toLowerCase()}`}
            </p>
            <Button
              type="button"
              onClick={onCreateAd}
              title={postAdBlockedByUnverifiedEmail ? postAdEmailBlockTitle : undefined}
              aria-disabled={postAdBlockedByUnverifiedEmail}
              className={postAdBlockedByUnverifiedEmail ? "opacity-60 saturate-50" : undefined}
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Anúncio
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAds.map((ad) => (
              <MyAdCard
                key={ad.id}
                ad={ad}
                actionLoadingId={actionLoadingId}
                onTogglePause={handleTogglePause}
                onMarkAsSold={handleMarkAsSold}
                onDelete={handleDelete}
                onEdit={() => setEditingAd(ad)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => {
          if (tab === "home" || tab === "favorites") onTabChange(tab);
        }}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />

      {/* Edit Ad Modal */}
      {editingAd && (
        <EditAdModal
          ad={editingAd}
          onClose={() => setEditingAd(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-slide-up">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Anúncio atualizado com sucesso!</span>
        </div>
      )}
    </div>
  );
}

interface MyAdCardProps {
  ad: MyAd;
  actionLoadingId: string | null;
  onTogglePause: (id: string) => void;
  onMarkAsSold: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
}

function MyAdCard({ ad, actionLoadingId, onTogglePause, onMarkAsSold, onDelete, onEdit }: MyAdCardProps) {
  const isActionLoading = actionLoadingId === ad.id;
  const [qualityDetail, setQualityDetail] = useState<ListingQualityResult | null>(null);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [qualityError, setQualityError] = useState<string | null>(null);
  const pendingQuality = !hasPersistedListingQuality(ad.listingQualityScore, ad.listingQualityVersion);
  const displayQualityScore = qualityDetail?.score ?? ad.listingQualityScore;
  const showImproveLink =
    !qualityOpen &&
    shouldShowListingQualityImproveLink(displayQualityScore, pendingQuality);

  const handleQualityToggle = useCallback(async (open: boolean) => {
    setQualityOpen(open);
    if (!open) return;
    if (qualityDetail) return;
    setQualityLoading(true);
    setQualityError(null);
    try {
      const res = await getListingQualityForAd(ad.id);
      setQualityDetail(res);
    } catch (e) {
      setQualityError(e instanceof Error ? e.message : "Não foi possível carregar a qualidade");
    } finally {
      setQualityLoading(false);
    }
  }, [ad.id, qualityDetail]);

  useEffect(() => {
    setQualityDetail(null);
    setQualityOpen(false);
    setQualityLoading(false);
    setQualityError(null);
  }, [
    ad.id,
    ad.title,
    ad.description,
    ad.listingQualityScore,
    ad.listingQualityTier,
    ad.listingQualityVersion,
  ]);

  /** Pausa pelo backend (swap com 1.º pagamento); não permitir reativar manualmente. */
  const swapReservationLocked = ad.status === "paused" && ad.apiListingStatus === "paused";
  const categoryLine = formatAdCategoryDisplayLine(ad);
  const getStatusBadge = () => {
    const badges = {
      active: { label: "Ativo", className: "bg-green-100 text-green-700 border-green-200" },
      paused: { label: "Pausado", className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
      sold: { label: "Vendido", className: "bg-blue-100 text-blue-700 border-blue-200" },
      expired: { label: "Expirado", className: "bg-gray-100 text-gray-700 border-gray-200" },
    };

    const badge = badges[ad.status];
    const label = swapReservationLocked ? "Pausado (troca)" : badge.label;

    return (
      <span
        className={`inline-flex shrink-0 whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium border ${badge.className}`}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg hover:shadow-md transition-shadow overflow-visible">
      <div className="flex flex-col sm:flex-row gap-4 p-4">
        {/* Estado alinhado à direita da coluna da imagem (igual ao mobile). */}
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-48">
          <div className="flex w-full flex-col items-end gap-1">
            {getStatusBadge()}
            {ad.status === "active" && ad.discoveryEligible === false && (
              <span className="inline-flex shrink-0 whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-medium border bg-amber-50 text-amber-800 border-amber-200">
                Não aparece na busca
              </span>
            )}
          </div>
          <div className="h-48 w-full overflow-hidden rounded-lg">
            <AdImage
              src={getAdImage(ad)}
              alt={ad.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium truncate">{ad.title}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => void shareAdListing({ adId: ad.id, title: ad.title, price: ad.price })}
                  className="shrink-0 p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                  aria-label="Compartilhar anúncio"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
              {categoryLine ? (
                <div className="mb-2 flex min-w-0 items-start gap-1.5 text-xs text-muted-foreground">
                  <Tag className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0 truncate" title={categoryLine}>
                    {categoryLine}
                  </span>
                </div>
              ) : null}
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                {ad.description}
              </p>
              <p className="text-lg font-medium text-primary">
                {formatCurrency(ad.price)}
              </p>
              {swapReservationLocked && (
                <p className="text-xs text-muted-foreground mt-2">
                  Reservado num pedido de troca. A reativação fica disponível após o pagamento da outra parte ou se o pedido for cancelado.
                </p>
              )}
            </div>
          </div>

          {/* Stats + quality */}
          <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{ad.views} visualizações</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="w-4 h-4" />
                <span>{ad.favorites} favoritados</span>
              </div>
            </div>
            <div className="min-w-0 sm:max-w-[55%] sm:text-right">
              <ListingQualityPanel
                inline
                compact
                collapsibleChecklist
                checklistOpen={qualityOpen}
                onChecklistOpenChange={(open) => void handleQualityToggle(open)}
                result={qualityOpen ? qualityDetail : null}
                loading={qualityLoading}
                error={qualityError}
                persistedScore={ad.listingQualityScore}
                persistedTier={ad.listingQualityTier}
                pendingQuality={pendingQuality}
              />
              {!showImproveLink ? null : (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline mt-0.5 sm:float-right"
                  onClick={() => void handleQualityToggle(true)}
                >
                  Ver como melhorar
                </button>
              )}
            </div>
          </div>

          {/* Actions: sold = only Excluir; otherwise Editar + Pausar/Reativar + Marcar como Vendido + Excluir */}
          <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-start">
            {ad.status !== "sold" && (
              <>
                <button
                  onClick={() => onEdit(ad.id)}
                  disabled={isActionLoading}
                  className="inline-flex min-h-10 items-center justify-center gap-1.5 px-2 py-2 text-sm sm:w-auto sm:px-3 bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Edit className="w-4 h-4 shrink-0" />
                  Editar
                </button>
                {ad.status === "active" && (
                  <>
                    <button
                      onClick={() => onTogglePause(ad.id)}
                      disabled={isActionLoading}
                      className="inline-flex min-h-10 items-center justify-center gap-1.5 px-2 py-2 text-sm sm:w-auto sm:px-3 bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Pause className="w-4 h-4 shrink-0" />}
                      Pausar
                    </button>
                    <button
                      onClick={() => onMarkAsSold(ad.id)}
                      disabled={isActionLoading}
                      className="col-span-2 inline-flex min-h-10 items-center justify-center gap-1.5 px-2 py-2 text-sm sm:col-auto sm:w-auto sm:px-3 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <CheckCircle className="w-4 h-4 shrink-0" />}
                      Marcar como Vendido
                    </button>
                  </>
                )}
                {ad.status === "paused" && !swapReservationLocked && (
                  <button
                    onClick={() => onTogglePause(ad.id)}
                    disabled={isActionLoading}
                    className="inline-flex min-h-10 items-center justify-center gap-1.5 px-2 py-2 text-sm sm:w-auto sm:px-3 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Play className="w-4 h-4 shrink-0" />}
                    Reativar
                  </button>
                )}
              </>
            )}

            <button
              onClick={() => onDelete(ad.id)}
              disabled={isActionLoading}
              className={`inline-flex min-h-10 items-center justify-center gap-1.5 px-2 py-2 text-sm sm:w-auto sm:px-3 text-destructive hover:bg-destructive/10 rounded-lg transition-colors disabled:opacity-50 ${
                ad.status !== "sold" ? "col-span-2 sm:col-auto" : "col-span-2"
              }`}
            >
              {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Trash2 className="w-4 h-4 shrink-0" />}
              Excluir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}